import React, { useState } from 'react';
import { useMode } from '../../context/ModeContext';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { 
  FiUser, 
  FiUsers, 
  FiChevronDown, 
  FiCheck,
  FiArrowRight,
  FiHome,
  FiGrid,
  FiLoader,
  FiShield,
  FiBox
} from 'react-icons/fi';

/**
 * ModeSwitch Component - Allows users to switch between Individual and Organization modes
 * 
 * Features:
 * - Visual mode indicator
 * - Dropdown for mode selection
 * - Organization selection when multiple orgs available
 * - Smooth transitions with loading states
 * - Context-aware availability
 */
const ModeSwitch = ({ className = '', showLabel = true, variant = 'default', shortSidebarLabel = false }) => {
  const { 
    currentMode, 
    availableModes, 
    isModeSwitching,
    switchToIndividualMode,
    switchToOrganizationMode,
    switchToAdminMode,
    switchMode,
    getModeConfig,
    hasOrganizationAccess,
    hasAdminAccess,
    tryAccessOrganizationMode
  } = useMode();
  
  const { currentUser } = useAuth();
  const { currentOrganization, organizations } = useOrganization();
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const modeConfig = getModeConfig();

  // Don't show mode switch if only individual mode is available
  if (availableModes.length <= 1) {
    return null;
  }

  const handleModeSwitch = (targetMode, organizationId = null) => {
    setIsDropdownOpen(false);
    
    // Use safer access method for organization mode
    if (targetMode === 'organization') {
      tryAccessOrganizationMode();
    } else {
      switchMode(targetMode);
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'individual':
        return FiUser;
      case 'organization':
        return FiBox;
      case 'platform_admin':
      case 'admin':
        return FiShield;
      default:
        return FiUser;
    }
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 'individual':
        return 'Personal Workspace';
      case 'organization':
        return 'Organization Workspace';
      case 'platform_admin':
        return 'Platform Admin';
      case 'admin':
        return 'Admin Workspace';
      default:
        return 'Workspace';
    }
  };

  const getModeDescription = (mode) => {
    switch (mode) {
      case 'individual':
      case 'organization':
        return '';
      case 'platform_admin':
        return 'Platform-wide controls';
      case 'admin':
        return 'Platform controls';
      default:
        return '';
    }
  };

  const sidebarButtonLabel = (mode) => {
    if (shortSidebarLabel) {
      if (mode === 'individual') return 'Personal';
      if (mode === 'organization') return 'Organization';
      if (mode === 'platform_admin') return 'Platform';
      if (mode === 'admin') return 'Admin';
    }
    return getModeLabel(mode);
  };

  const getModeAccentClass = (mode, isActive = false) => {
    if (mode === 'individual') {
      return isActive ? 'bg-blue-100 text-blue-600' : 'bg-blue-100 text-blue-600';
    }
    if (mode === 'organization') {
      return isActive ? 'bg-purple-100 text-purple-600' : 'bg-purple-100 text-purple-600';
    }
    if (mode === 'platform_admin') {
      return isActive ? 'bg-violet-100 text-violet-600' : 'bg-violet-100 text-violet-600';
    }
    return isActive ? 'bg-red-100 text-red-600' : 'bg-red-100 text-red-600';
  };

  // Sidebar variant: personal workspace (blue sidebar)
  if (variant === 'sidebar') {
    return (
      <div className={`relative ${className}`}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={isModeSwitching}
          title={getModeLabel(currentMode)}
          className={`flex w-full min-w-0 items-center gap-2 rounded-xl px-3 py-2.5 text-left transition-all disabled:opacity-50 border shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] ${
            isDropdownOpen
              ? 'border-white/35 bg-white/20'
              : 'border-white/20 bg-white/10 hover:bg-white/15'
          } backdrop-blur-md`}
        >
          {isModeSwitching ? (
            <FiLoader className="h-4 w-4 shrink-0 animate-spin text-blue-100" />
          ) : (
            React.createElement(getModeIcon(currentMode), {
              className: 'h-4 w-4 shrink-0 text-blue-100'
            })
          )}
          {showLabel && (
            <span className="min-w-0 flex-1 text-left text-xs font-semibold tracking-wide text-white">
              {sidebarButtonLabel(currentMode)}
            </span>
          )}
          <FiChevronDown className="h-3 w-3 shrink-0 text-blue-100/80" />
        </button>

        {isDropdownOpen && (
          <div className="absolute left-0 right-0 top-full z-[60] mt-1.5 min-w-[11rem] rounded-xl border border-slate-600/90 bg-slate-950/98 py-1.5 shadow-2xl ring-1 ring-black/30 backdrop-blur-md">
            <div className="px-1.5">
              {availableModes.map((mode) => {
                const description = getModeDescription(mode);
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => handleModeSwitch(mode)}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                      currentMode === mode
                        ? 'bg-blue-600/35 text-white'
                        : 'text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    {React.createElement(getModeIcon(mode), {
                      className: `h-4 w-4 shrink-0 ${currentMode === mode ? 'text-blue-200' : 'text-slate-400'}`
                    })}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold tracking-wide">{getModeLabel(mode)}</div>
                      {description && (
                        <div className="text-[11px] text-slate-400">{description}</div>
                      )}
                    </div>
                    {currentMode === mode && <FiCheck className="h-4 w-4 shrink-0 text-blue-300" />}
                  </button>
                );
              })}
            </div>

            {currentMode === 'organization' && organizations && organizations.length > 1 && (
              <div className="mt-1 border-t border-slate-700/90 px-1.5 pt-1.5">
                <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  Switch organization
                </div>
                {organizations.map((org) => (
                  <button
                    key={org._id}
                    type="button"
                    onClick={() => handleModeSwitch('organization', org._id)}
                    className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors ${
                      currentOrganization?._id === org._id
                        ? 'bg-violet-600/30 text-white'
                        : 'text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    <FiGrid className="h-4 w-4 shrink-0 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold">{org.name}</div>
                      <div className="truncate text-[11px] text-slate-400">{org.domain}</div>
                    </div>
                    {currentOrganization?._id === org._id && <FiCheck className="h-4 w-4 shrink-0 text-violet-300" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Sidebar variant: platform admin workspace (violet sidebar — matches personal/org switcher pattern)
  if (variant === 'platformSidebar') {
    return (
      <div className={`relative ${className}`}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={isModeSwitching}
          title={getModeLabel(currentMode)}
          className={`flex w-full min-w-0 items-center gap-2 rounded-xl px-3 py-2.5 text-left transition-all disabled:opacity-50 border shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] ${
            isDropdownOpen
              ? 'border-violet-300/35 bg-white/15'
              : 'border-violet-300/20 bg-white/10 hover:bg-white/[0.14]'
          } backdrop-blur-md`}
        >
          {isModeSwitching ? (
            <FiLoader className="h-4 w-4 shrink-0 animate-spin text-violet-100" />
          ) : (
            React.createElement(getModeIcon(currentMode), {
              className: 'h-4 w-4 shrink-0 text-violet-100',
            })
          )}
          {showLabel && (
            <span className="min-w-0 flex-1 text-left text-xs font-semibold tracking-wide text-violet-50">
              {sidebarButtonLabel(currentMode)}
            </span>
          )}
          <FiChevronDown className="h-3 w-3 shrink-0 text-violet-100/80" />
        </button>

        {isDropdownOpen && (
          <div className="absolute left-0 right-0 top-full z-[60] mt-1.5 min-w-[12rem] rounded-xl border border-slate-600/90 bg-slate-950/98 py-1.5 shadow-2xl ring-1 ring-black/30 backdrop-blur-md">
            <div className="px-1.5">
              <div className="px-2 pb-1.5 pt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Switch workspace
              </div>
              {availableModes.map((mode) => {
                const description = getModeDescription(mode);
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => handleModeSwitch(mode)}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                      currentMode === mode
                        ? 'bg-violet-600/35 text-white'
                        : 'text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    {React.createElement(getModeIcon(mode), {
                      className: `h-4 w-4 shrink-0 ${currentMode === mode ? 'text-violet-200' : 'text-slate-400'}`,
                    })}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold tracking-wide">{getModeLabel(mode)}</div>
                      {description && (
                        <div className="text-[11px] text-slate-400">{description}</div>
                      )}
                    </div>
                    {currentMode === mode && <FiCheck className="h-4 w-4 shrink-0 text-violet-300" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Compact variant for mobile/small spaces
  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={isModeSwitching}
          className={`flex items-center space-x-2 px-3 py-2 bg-white dark:bg-slate-900 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 border ${
            isDropdownOpen
              ? 'border-blue-400 shadow-sm dark:border-blue-500'
              : 'border-gray-200 dark:border-gray-700'
          }`}
        >
          {isModeSwitching ? (
            <FiLoader className="w-4 h-4 animate-spin text-blue-600" />
          ) : (
            React.createElement(getModeIcon(currentMode), { 
              className: "w-4 h-4 text-gray-600" 
            })
          )}
          {showLabel && (
            <span className="text-xs font-medium text-gray-700 dark:text-gray-100">
              {getModeLabel(currentMode)}
            </span>
          )}
          <FiChevronDown className="w-3 h-3 text-gray-400" />
        </button>

        {isDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-full min-w-[10rem] bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl ring-1 ring-blue-100/70 dark:ring-blue-500/40 z-50">
            <div className="p-2">
              {availableModes.map((mode) => {
                const description = getModeDescription(mode);
                return (
                <button
                  key={mode}
                  onClick={() => handleModeSwitch(mode)}
                  className={`w-full flex items-center space-x-3 px-3 py-1.5 rounded-md transition-colors ${
                    currentMode === mode
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/20 dark:text-blue-100'
                      : 'hover:bg-gray-50 text-gray-700 dark:hover:bg-gray-700 dark:text-gray-100'
                  }`}
                >
                  {React.createElement(getModeIcon(mode), { 
                    className: `w-4 h-4 ${currentMode === mode ? 'text-blue-600 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'}` 
                  })}
                  <div className="flex-1 text-left">
                    <div className="text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-100">
                      {getModeLabel(mode)}
                    </div>
                    {description && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{description}</div>
                    )}
                  </div>
                  {currentMode === mode && (
                    <FiCheck className="w-4 h-4 text-blue-600" />
                  )}
                  </button>
                );
              })}
            </div>

            {/* Organization selection if multiple organizations */}
            {currentMode === 'organization' && organizations && organizations.length > 1 && (
              <div className="border-t border-gray-100 p-2">
                <div className="text-[11px] text-gray-500 px-3 py-1 mb-1">Switch Organization</div>
                {organizations.map((org) => (
                  <button
                    key={org._id}
                    onClick={() => handleModeSwitch('organization', org._id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md transition-colors ${
                      currentOrganization?._id === org._id
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <FiGrid className="w-4 h-4 text-gray-500" />
                    <div className="flex-1 text-left">
                      <div className="text-xs font-semibold">{org.name}</div>
                      <div className="text-xs text-gray-500">{org.domain}</div>
                    </div>
                    {currentOrganization?._id === org._id && (
                      <FiCheck className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default variant with full styling
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        disabled={isModeSwitching}
        className={`flex items-center space-x-3 px-4 py-3 bg-white dark:bg-gray-800 rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 min-w-[200px] border ${
          isDropdownOpen
            ? 'border-blue-400 shadow-lg ring-1 ring-blue-200/60 dark:border-blue-500 dark:ring-blue-500/40'
            : 'border-gray-200 dark:border-gray-700'
        }`}
      >
  <div className="flex items-center space-x-3 flex-1">
          <div className={`p-2 rounded-lg ${
            currentMode === 'individual' 
              ? 'bg-blue-100 text-blue-600' 
              : currentMode === 'organization'
                ? 'bg-purple-100 text-purple-600'
                : currentMode === 'platform_admin'
                  ? 'bg-violet-100 text-violet-600'
                : 'bg-red-100 text-red-600'
          }`}>
            {isModeSwitching ? (
              <FiLoader className="w-5 h-5 animate-spin" />
            ) : (
              React.createElement(getModeIcon(currentMode), { className: "w-5 h-5" })
            )}
          </div>
          
          <div className="flex-1 text-left">
            <div className="text-sm font-semibold text-gray-900">
              {modeConfig.title}
            </div>
            <div className="text-xs text-gray-500">
              {modeConfig.subtitle}
            </div>
          </div>
        </div>
        
        <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
          isDropdownOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {isDropdownOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsDropdownOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl ring-1 ring-blue-100/60 dark:ring-blue-500/40 z-50 overflow-hidden">
            <div className="p-3">
              <div className="text-[11px] font-medium text-gray-500 mb-3 px-1">
                Switch Workspace
              </div>
              
              {availableModes.map((mode) => (
                <button
                  key={mode}
                  onClick={() => handleModeSwitch(mode)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 mb-1 last:mb-0 ${
                    currentMode === mode
                      ? 'bg-blue-50 border border-blue-200 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    currentMode === mode 
                      ? getModeAccentClass(mode, true)
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {React.createElement(getModeIcon(mode), { className: "w-4 h-4" })}
                  </div>
                  
                  <div className="flex-1 text-left">
                    <div className="text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-100">
                      {getModeLabel(mode)}
                    </div>
                    {getModeDescription(mode) && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{getModeDescription(mode)}</div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {currentMode === mode ? (
                      <div className="flex items-center space-x-1 text-blue-600">
                        <FiCheck className="w-4 h-4" />
                        <span className="text-xs font-medium">Active</span>
                      </div>
                    ) : (
                      <FiArrowRight className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Organization selection section */}
            {hasOrganizationAccess && organizations && organizations.length > 1 && (
              <div className="border-t border-gray-100 p-3">
                <div className="text-[11px] font-medium text-gray-500 mb-3 px-1">
                  Available Organizations
                </div>
                
                {organizations.map((org) => (
                  <button
                    key={org._id}
                    onClick={() => handleModeSwitch('organization', org._id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors mb-1 last:mb-0 ${
                      currentOrganization?._id === org._id && currentMode === 'organization'
                        ? 'bg-purple-50 border border-purple-200 text-purple-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FiGrid className="w-4 h-4 text-gray-500" />
                    </div>
                    
                    <div className="flex-1 text-left">
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-100">{org.name}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{org.domain || 'Organization'}</div>
                    </div>
                    
                    {currentOrganization?._id === org._id && currentMode === 'organization' && (
                      <FiCheck className="w-4 h-4 text-purple-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ModeSwitch;